import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

import { ASSISTANTS_CATEGORY, GENIE_EVALUATE_TYPE } from '../../constants';
import asyncPool from '../../service/asyncPool';
import callApi from '../../service/callApi';
import sendRequest from '../../service/sendRequest';
import { history } from '../../store';
import { enqueueSnackbar, somethingWrongNotification } from '../notifier/notifierActions';
import {
  cancelEvaluationByType,
  setEvalAbortController,
  updateEvaluatedGeneratedMessages,
  updateEvaluatedPredictedMessages,
} from './genieReducer';

const GET_GENIE_DATA = 'genie/GET_GENIE_DATA';
const PREDICT_RESPONSE = 'genie/PREDICT_RESPONSE';
const GENERATE_ALTERNATE = 'genie/GENERATE_ALTERNATE';
const RESET_SESSION = 'genie/RESET_SESSION';
const REPLY_GENIE = 'genie/REPLY_GENIE';
const GENERATE_MESSAGES = 'genie/GENERATE_MESSAGES';
const GET_BRAND_OPTIONS = 'genie/GET_BRAND_OPTIONS';
const EVALUATE_ALTERNATE_MESSAGES = 'genie/EVALUATE_ALTERNATE_MESSAGES';
const EVALUATE_GENERATED_MESSAGES = 'genie/EVALUATE_GENERATED_MESSAGES';
const CREATE_CUSTOM_MESSAGES = 'genie/CREATE_CUSTOM_MESSAGES';
const GET_GENIE_AUDIENCES = 'genie/GET_GENIE_AUDIENCES';

export const getGenieData = createAsyncThunk(GET_GENIE_DATA, async ({ id }, { rejectWithValue }) => {
  const response = await sendRequest(() => callApi.get(`/api/genie/${id}`), {
    onError: (e) => {
      history.push('/projects');
      return rejectWithValue(e);
    },
  });

  return { projectId: id, ...response.data };
});

export const predictResponse = createAsyncThunk(
  PREDICT_RESPONSE,
  async ({ values, projectId }, { rejectWithValue }) => {
    const { message, audienceId } = values;

    const response = await sendRequest(
      () =>
        callApi.post(`/api/genie/predict/bunch?id=${projectId}`, {
          message: [message],
          ...(audienceId ? { audienceId } : {}),
        }),
      { onError: (e) => rejectWithValue(e) },
    );

    return response.data[0];
  },
);

export const generateAlternate = createAsyncThunk(GENERATE_ALTERNATE, async ({ values, projectId }, { dispatch }) => {
  const { message, url } = values;

  dispatch(cancelEvaluationByType(GENIE_EVALUATE_TYPE.ALTERNATE));

  const response = await callApi.post(`/api/genie/predict/messages?id=${projectId}`, {
    message,
    url,
  });

  dispatch(evaluateAlternateMessages({ values, projectId, messages: response.data }));

  return response.data;
});

const sendMessagesEvaluation = async ({ messages, projectId, commonData, signal }) => {
  try {
    const payload = { message: [...messages], ...commonData };
    const response = await callApi.post(`/api/genie/predict/bunch?id=${projectId}`, payload, { signal });
    return response.data;
  } catch (error) {
    if (signal.aborted) throw new Error('Request cancelled');
    throw error;
  }
};

export const evaluateAlternateMessages = createAsyncThunk(
  EVALUATE_ALTERNATE_MESSAGES,
  async ({ values, projectId, messages }, { rejectWithValue, dispatch }) => {
    const { audienceId } = values;
    const commonData = { ...(audienceId ? { audienceId } : {}) };

    const controller = new AbortController();
    dispatch(setEvalAbortController({ controller, type: GENIE_EVALUATE_TYPE.ALTERNATE }));

    try {
      const response = await sendMessagesEvaluation({ messages, projectId, commonData, signal: controller.signal });

      dispatch(updateEvaluatedPredictedMessages(response));
      dispatch(setEvalAbortController({ controller: null, type: GENIE_EVALUATE_TYPE.ALTERNATE }));
    } catch (error) {
      dispatch(cancelEvaluationByType(GENIE_EVALUATE_TYPE.ALTERNATE));

      if (controller.signal.aborted) return rejectWithValue('Cancelled');
      else dispatch(somethingWrongNotification());
      return rejectWithValue(error.message);
    }
  },
);

export const evaluateGeneratedMessages = createAsyncThunk(
  EVALUATE_GENERATED_MESSAGES,
  async ({ values, projectId, messages }, { rejectWithValue, dispatch }) => {
    const { url, audienceId } = values;
    const commonData = { url, ...(audienceId ? { audienceId } : {}) };

    const controller = new AbortController();
    dispatch(setEvalAbortController({ controller, type: GENIE_EVALUATE_TYPE.GENERATED }));

    try {
      const response = await sendMessagesEvaluation({ messages, projectId, commonData, signal: controller.signal });

      dispatch(updateEvaluatedGeneratedMessages(response));
      dispatch(setEvalAbortController({ controller: null, type: GENIE_EVALUATE_TYPE.GENERATED }));
    } catch (error) {
      dispatch(cancelEvaluationByType(GENIE_EVALUATE_TYPE.GENERATED));

      if (controller.signal.aborted) return rejectWithValue('Cancelled');
      else dispatch(somethingWrongNotification());
      return rejectWithValue(error.message);
    }
  },
);

export const resetSession = createAsyncThunk(RESET_SESSION, async (_, { rejectWithValue, getState, dispatch }) => {
  const threadId = getState().genie.threadId;

  if (!threadId) {
    return rejectWithValue(null);
  }

  dispatch(cancelEvaluationByType(GENIE_EVALUATE_TYPE.ALTERNATE));
  dispatch(cancelEvaluationByType(GENIE_EVALUATE_TYPE.GENERATED));

  const response = await sendRequest(() => callApi.post(`/api/ai/playground/open-ai/reset?threadId=${threadId}`), {
    onError: (e) => rejectWithValue(e),
  });

  return response.data;
});

export const replyGenie = createAsyncThunk(
  REPLY_GENIE,
  async ({ id, reply, assistantId, generateFormValues }, { rejectWithValue, getState, dispatch }) => {
    const storedThreadId = getState().genie.threadId;

    const response = await sendRequest(
      () =>
        callApi.post(`/api/genie/reply?id=${id}`, {
          reply,
          assistantId,
          threadId: storedThreadId,
        }),
      {
        onError: (e) => rejectWithValue(e),
      },
    );

    const storedProjectId = getState().genie.projectId;

    if (id !== storedProjectId) {
      return rejectWithValue(null);
    }

    dispatch(
      evaluateGeneratedMessages({ values: generateFormValues, projectId: id, messages: response.data.messages }),
    );

    return response.data;
  },
);

export const generateMessages = createAsyncThunk(
  GENERATE_MESSAGES,
  async ({ id, values }, { rejectWithValue, dispatch, getState }) => {
    const { byPass, focusOn, url, channels, fileText } = values;

    const assistantId = channels[ASSISTANTS_CATEGORY.GENERATE_ALL].assistant?.id;

    if (!id || !assistantId || !url) {
      return rejectWithValue(null);
    }

    dispatch(cancelEvaluationByType(GENIE_EVALUATE_TYPE.GENERATED));

    const response = await sendRequest(
      (cancelToken) =>
        callApi.post(
          `/api/genie/url?id=${id}`,
          {
            assistantId,
            byPass,
            focusOn,
            url,
            fileText,
          },
          { cancelToken },
        ),
      {
        onError: (e) => {
          dispatch(somethingWrongNotification());
          return rejectWithValue(e);
        },
      },
    );

    const storedProjectId = getState().genie.projectId;

    if (id !== storedProjectId) {
      return rejectWithValue(null);
    }

    dispatch(evaluateGeneratedMessages({ values, projectId: id, messages: response.data.messages }));

    return response.data;
  },
);

export const getGenieBrandOptions = createAsyncThunk(GET_BRAND_OPTIONS, async (_, { rejectWithValue }) => {
  const response = await sendRequest(() => axios.get(`/api/consumer/genie/brands`), {
    onError: (e) => rejectWithValue(e),
  });

  return response.data;
});

export const createCustomMessages = createAsyncThunk(
  CREATE_CUSTOM_MESSAGES,
  async ({ projectId, messages }, { dispatch, getState, rejectWithValue }) => {
    const formattedMessages = messages.map(({ message }) => message);

    const url = getState().genie.generationForm.url;

    if (!url) {
      dispatch(
        enqueueSnackbar({
          message: 'urlIsRequired',
          options: {
            variant: 'error',
          },
        }),
      );
      return rejectWithValue(null);
    }

    dispatch(
      evaluateGeneratedMessages({
        values: {
          url,
        },
        projectId,
        messages: formattedMessages,
      }),
    );

    return formattedMessages;
  },
);

export const getGenieAudiences = createAsyncThunk(GET_GENIE_AUDIENCES, async ({ projectId }, { rejectWithValue }) => {
  const response = await sendRequest(() => callApi.get(`/api/genie/audiences?projectId=${projectId}`), {
    onError: (e) => rejectWithValue(e),
  });

  return response.data;
});
